<script setup lang="ts">
import { computed, ref, onMounted, watch, nextTick } from 'vue'
import SalarySummary from './SalarySummary.vue'
import SalaryDistributionChart from './SalaryDistributionChart.vue'
import SalaryPeriodSelector from './SalaryPeriodSelector.vue'
import { useGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'
import { stateOptions } from '~/utils/states'
import { formatCurrency } from '~/utils/formatters'

interface Props {
  companyName: string
  permalink: string
  selectedRole?: string
  selectedState?: string
  selectedCity?: string
  preFetchedData?: any
  isLoading?: boolean
  isSsrLoading?: boolean
  forceServerRender?: boolean
  locationName?: string
  descriptionText?: string
}
const {
  companyName,
  permalink,
  selectedRole = '',
  selectedState = '',
  selectedCity = '',
  preFetchedData = null,
  isLoading = false,
  isSsrLoading = false,
  forceServerRender = false,
  locationName = undefined,
  descriptionText = ''
} = defineProps<Props>()
const currentRole = ref(selectedRole)
const currentState = ref(selectedState)
const currentCity = ref(selectedCity)
watch(
  () => selectedRole,
  newVal => (currentRole.value = newVal)
)
watch(
  () => selectedState,
  newVal => (currentState.value = newVal)
)
watch(
  () => selectedCity,
  newVal => (currentCity.value = newVal)
)
const processedPreFetchedData = computed(() => {
  if (preFetchedData && 'data' in preFetchedData) {
    return preFetchedData.data
  }
  if (preFetchedData && 'adjusted_salary' in preFetchedData) {
    return preFetchedData
  }
  return null
})

const salaryData = ref<any>(null)
const isLoadingRef = ref(isLoading)
const error = ref<string | null>(null)
const componentKey = ref(0)
const hasFetchedOnClient = ref(false)

const { activePeriod, displayPeriods, changePeriod, adjustValueForPeriod }
  = useGlobalSalaryPeriod()
const currentFilterKey = computed(
  () =>
    `${currentRole.value || 'all'}-${currentState.value || 'all'}-${currentCity.value || 'all'
    }`
)

// Initialize salaryData from preFetchedData if available
if (processedPreFetchedData.value) {
  salaryData.value = processedPreFetchedData.value
  isLoadingRef.value = false
}

async function fetchSalaryData() {
  if (processedPreFetchedData.value) {
    salaryData.value = processedPreFetchedData.value
    isLoadingRef.value = false
    return
  }

  if (hasFetchedOnClient.value && salaryData.value) {
    isLoadingRef.value = false
    return
  }

  if (import.meta.server) {
    return
  }

  isLoadingRef.value = true
  error.value = null
  try {
    const response = await $fetch(
      `/api/companies/${permalink}/salaries/detail`,
      {
        method: 'POST',
        body: {
          role: currentRole.value,
          state: currentState.value,
          city: currentCity.value
        }
      }
    )
    if (response.success && 'data' in response) {
      if (
        locationName
        && currentState.value
        && !currentCity.value
        && typeof response.data === 'object'
        && response.data !== null
        && 'location' in response.data
        && (response.data as { location?: string }).location === 'USA'
      ) {
        (response.data as { location?: string }).location = locationName
      }
      salaryData.value = response.data
      hasFetchedOnClient.value = true
    } else {
      if ('error' in response && response.error) {
        error.value = response.error
      } else {
        error.value = 'Failed to load salary data'
      }
    }
  } catch (err) {
    error.value = 'An error occurred while loading salary data'
  } finally {
    isLoadingRef.value = false
  }
}
watch(
  () => preFetchedData,
  (newPreFetchedData) => {
    if (newPreFetchedData) {
      const processedData
        = 'data' in newPreFetchedData
          ? newPreFetchedData.data
          : 'adjusted_salary' in newPreFetchedData
            ? newPreFetchedData
            : null
      if (processedData) {
        salaryData.value = processedData
        isLoadingRef.value = false
      }
    }
  },
  { immediate: true }
)

watch(
  currentFilterKey,
  async (newFilterKey, oldFilterKey) => {
    if (newFilterKey !== oldFilterKey) {
      componentKey.value++
      hasFetchedOnClient.value = false
      if (import.meta.client && !processedPreFetchedData.value) {
        salaryData.value = null
        await fetchSalaryData()
      }
    }
  }
)

onMounted(async () => {
  await nextTick()

  if (salaryData.value || processedPreFetchedData.value) {
    if (processedPreFetchedData.value && !salaryData.value) {
      salaryData.value = processedPreFetchedData.value
    }
    return
  }
  if (forceServerRender && !isSsrLoading) {
    return
  }

  if (!salaryData.value && !processedPreFetchedData.value && !hasFetchedOnClient.value) {
    fetchSalaryData()
  }
})
const chartMin = computed(() => {
  if (!salaryData.value) return 0
  return adjustValueForPeriod(salaryData.value.adjusted_salary.P10)
})
const chartMedian = computed(() => {
  if (!salaryData.value) return 0
  return adjustValueForPeriod(salaryData.value.adjusted_salary.P50)
})
const chartMax = computed(() => {
  if (!salaryData.value) return 0
  return adjustValueForPeriod(salaryData.value.adjusted_salary.P90)
})
const baseAverage = computed(() => {
  if (!salaryData.value) return 0
  return adjustValueForPeriod(salaryData.value.base_salary.P50)
})
const marketText = computed(() => {
  if (!salaryData.value || !salaryData.value.market_data)
    return 'at market average'
  const ratio = salaryData.value.market_data.company_ratio
  const percentage = Math.abs((ratio - 1) * 100).toFixed(1)
  if (ratio > 1) {
    return `${percentage}% above market average`
  } else if (ratio < 1) {
    return `${percentage}% below market average`
  }
  return 'at market average'
})
const locationText = computed(() => {
  if (locationName && currentState.value && !currentCity.value) {
    return ` in <strong>${locationName}</strong>`
  }
  if (currentCity.value && currentState.value) {
    return ` in <strong>${currentCity.value}</strong>, <strong>${currentState.value}</strong>`
  } else if (currentState.value) {
    return ` in <strong>${currentState.value}</strong>`
  }
  return ''
})
function formatLocation() {
  if (locationName && currentState.value && !currentCity.value) {
    return locationName
  }
  if (currentCity.value && currentState.value) {
    return `${currentCity.value}, ${currentState.value}`
  }
  if (currentState.value) {
    const state = stateOptions.find(s => s.code === currentState.value)
    return state?.label || currentState.value
  }
  return ''
}
</script>

<template>
  <Card :key="componentKey" class="salary-card min-h-[350px]" :ui="{ header: { padding: '' } }">
    <template #header>
      <SalaryPeriodSelector :active-period="activePeriod" @change-period="changePeriod" />
    </template>
    <div v-if="isLoadingRef" class="p-4 flex justify-center items-center min-h-[300px]">
      <Icon name="i-heroicons-arrow-path" class="animate-spin h-8 w-8 text-gray-400" />
    </div>
    <div v-else-if="error" class="p-4 text-center text-red-500 min-h-[300px] flex items-center justify-center">
      {{ error }}
    </div>
    <div v-else-if="salaryData" class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div class="flex flex-col justify-start w-full space-y-8">
        <SalarySummary :median="chartMedian" :period="activePeriod" :period-display="displayPeriods[activePeriod]"
          :base-average="baseAverage" :market-text="marketText" />
        <div class="text-sm text-gray-600">
          <p v-html="descriptionText" />
        </div>
      </div>
      <div class="flex items-center justify-center">
        <SalaryDistributionChart :median="chartMedian" :min="chartMin" :max="chartMax" :period="activePeriod"
          :format-value="formatCurrency" />
      </div>
    </div>
    <div v-else class="p-4 text-center text-gray-500 min-h-[300px] flex items-center justify-center">
      No salary data available
    </div>
  </Card>
</template>

<style scoped>
.salary-card :deep(.u-card-header) {
  padding: 0 !important;
}
</style>
